import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Checkbox,
  Pagination,
  Modal,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear"; // Import Clear Icon
import FormUserMessage from "./FormUserMessage";
import HttpClient from "../../../utils/HttpClient";

const UserMessage = () => {
  const [selectedRows, setSelectedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [usersData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginProfile, setLoginProfile] = useState();
  const rowsPerPage = 5;

  const handleSelect = (id) => {
    setSelectedRows((prevSelectedRows) => ({
      ...prevSelectedRows,
      [id]: !prevSelectedRows[id],
    }));
  };

  const handleSearch = () => {
    if (searchTerm.trim() === "") {
      // Reset filtered data to show all users
      setFilteredData(usersData);
    } else {
      const newFilteredData = usersData.filter(
        (row) =>
          row.personName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(newFilteredData);
    }
    setCurrentPage(1); // Reset to the first page after search
  };

  const handleClearSearch = () => {
    setSearchTerm(""); // Clear the search term
    setFilteredData(usersData); // Reset the filtered data to show all users
    setCurrentPage(1); // Reset the page to the first page
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSendMessage = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const selectedUserNames = Object.keys(selectedRows)
    .filter((id) => selectedRows[id])
    .map((id) => {
      const user = filteredData.find((user) => user._id === id);
      return user ? { _id: user._id, personName: user.personName } : null;
    })
    .filter((user) => user !== null);

  const selectedIds = selectedUserNames.map((user) => user._id);
  const selectedNames = selectedUserNames.map((user) => user.personName);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await HttpClient.requestData("user", "GET", {});
        if (res && res?.status) {
          setUserData(res?.data);
          setFilteredData(res?.data);
        } else {
          setError("Failed to fetch user data");
        }
      } catch (error) {
        setError("Error fetching user data");
      }
      setLoading(false);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchLoginProfile = async () => {
      try {
        const res = await HttpClient.requestData("view-profile", "GET", {});
        if (res && res?.status) {
          setLoginProfile(res?.data);
        } else {
          console.log(res?.message);
        }
      } catch (error) {
        console.error("Error fetching login profile", error);
      }
    };
    fetchLoginProfile();
  }, []);

  return (
    <div className="d-flex justify-content-end ml-4 mr-2">
      <div className="form-header">
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
          </Box>

          {loading ? (
            // Loader displayed during data fetch
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            // Error message if fetch fails
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <p style={{ color: "red" }}>{error}</p>
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Serial No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRows.map((item, index) => (
                    <TableRow key={item._id}>
                      <TableCell>{indexOfFirstRow + index + 1}</TableCell>
                      <TableCell>{item?.personName}</TableCell>
                      <TableCell>{item?.companyName}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={selectedRows[item?._id] || false}
                          onChange={() => handleSelect(item?._id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box
                sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={
                    !Object.values(selectedRows).some((selected) => selected)
                  }
                >
                  Send messages to selected user
                </Button>
              </Box>
            </>
          )}
        </Box>
      </div>

      {/* Modal for sending messages */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "100%",
            maxHeight: "100%",
            overflowX: "auto",
          }}
        >
          <FormUserMessage
            selectedNames={selectedNames}
            selectedIds={selectedIds}
            handleClose={handleCloseModal}
            loginProfile={loginProfile}
            setSelectedRows={setSelectedRows}
          />
        </Box>
      </Modal>
    </div>
  );
};

export default UserMessage;
